import { useEffect, useState, type ReactElement } from 'react';
import { humanizeApiError } from '@lapo/core';
import { OAuthConsent, type OAuthConsentScope } from '@lapo/ui';
import type { ApiClient } from './auth/apiClient.js';

/**
 * The OAuth authorization request parsed from `#/oauth/authorize?…`. Captured once (before the app's
 * hash-sync effect can rewrite the URL) and handed to this screen. The user is already signed in when
 * this renders, so approving binds the resulting token to their account.
 */
export interface ConsentRequest {
  clientId: string;
  redirectUri: string;
  scope: string;
  state?: string;
  codeChallenge: string;
  codeChallengeMethod: string;
}

/**
 * Parse an OAuth authorization request into a ConsentRequest, or null when it isn't one / is
 * incomplete. Two URL shapes arrive here (CONDUIT DL-13):
 *   - `#/oauth/authorize?client_id=…`   — params inside the fragment (Lapo's own integrations);
 *   - `?client_id=…#/oauth/authorize`   — params in the real query string, which is what a
 *     standards-compliant OAuth client (e.g. an MCP client like Claude Code) produces from the
 *     advertised fragment-style authorization_endpoint. Pass `location.search` as `search`.
 * `scope` is optional: an omitted scope is defaulted server-side to the MCP surface set (DL-5), and
 * the consent screen renders whatever the server reports it would grant.
 */
export function parseConsentRequest(hash: string, search = ''): ConsentRequest | null {
  const m = /^#\/?oauth\/authorize(?:\?(.*))?$/.exec(hash);
  if (!m) return null;
  // Fragment-embedded query wins when present; else fall back to the page's real query string.
  const q = new URLSearchParams(m[1] ?? (search.startsWith('?') ? search.slice(1) : search));
  const clientId = q.get('client_id') ?? '';
  const redirectUri = q.get('redirect_uri') ?? '';
  const codeChallenge = q.get('code_challenge') ?? '';
  const codeChallengeMethod = q.get('code_challenge_method') ?? 'S256';
  if (!clientId || !redirectUri || !codeChallenge) return null;
  return {
    clientId,
    redirectUri,
    scope: q.get('scope') ?? '',
    state: q.get('state') ?? undefined,
    codeChallenge,
    codeChallengeMethod,
  };
}

/** Append query params to a redirect URI, preserving any it already carries. */
function withParams(uri: string, params: Record<string, string | undefined>): string {
  const url = new URL(uri);
  for (const [k, v] of Object.entries(params)) if (v != null) url.searchParams.set(k, v);
  return url.toString();
}

export interface OAuthConsentScreenProps {
  api: ApiClient;
  request: ConsentRequest;
  /** The signed-in user's email — shown so they confirm which account they're authorizing. */
  userEmail?: string;
}

/**
 * Container for the consent screen: loads the request metadata (client name + scope copy), then on
 * Approve mints an authorization code and redirects the browser back to the client with `code`+`state`;
 * on Deny redirects back with `error=access_denied` (OAuth's user-cancelled response).
 */
export function OAuthConsentScreen({ api, request, userEmail }: OAuthConsentScreenProps): ReactElement {
  const [clientName, setClientName] = useState<string | undefined>();
  const [scopes, setScopes] = useState<OAuthConsentScope[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const info = await api.getOAuthConsent(request.clientId, request.redirectUri, request.scope);
        if (!alive) return;
        setClientName(info.clientName);
        setScopes(info.scopes);
      } catch (err) {
        if (alive) setError(humanizeApiError(err));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [api, request.clientId, request.redirectUri, request.scope]);

  const onApprove = (): void => {
    setBusy(true);
    void (async () => {
      try {
        const { redirect } = await api.approveOAuth({
          clientId: request.clientId,
          redirectUri: request.redirectUri,
          scope: request.scope,
          state: request.state,
          codeChallenge: request.codeChallenge,
          codeChallengeMethod: request.codeChallengeMethod,
        });
        window.location.assign(redirect);
      } catch (err) {
        setBusy(false);
        setError(humanizeApiError(err));
      }
    })();
  };

  const onDeny = (): void => {
    // OAuth's user-cancelled response — hand control back to the client so it can show its own error.
    window.location.assign(
      withParams(request.redirectUri, { error: 'access_denied', state: request.state }),
    );
  };

  return (
    <OAuthConsent
      userEmail={userEmail}
      clientName={clientName}
      scopes={scopes}
      loading={loading}
      error={error}
      busy={busy}
      onApprove={onApprove}
      onDeny={onDeny}
    />
  );
}
